import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    Dialog,
    DialogBody,
    DialogRoot,
    LoadingPanel,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import {
    apiQuery,
    isSiteUpgradeRequiredForFeature,
    SiteContextProvider,
    useCurrentOrganization,
} from '../../state';
import { useSpacePrimarySite } from '../Organization';
import { AdaptiveContentBuilderDialog } from './AdaptiveContentBuilderDialog';
import { AdaptiveContentFeatureIntro } from './AdaptiveContentFeatureIntro';

interface SpaceSiteAdaptiveContextValue {
    site: api.Site | undefined;
    isUpgradeRequired: boolean;
    openFeatureIntroDialog: (source: EnableAdaptiveContentFeatureSource) => void;
    openEditSchemaDialog: () => void;
}
const contextDefaultValue = {
    site: undefined,
    isUpgradeRequired: true,
    openFeatureIntroDialog: (_source) => {},
    openEditSchemaDialog: () => {},
};

type EnableAdaptiveContentFeatureSource = Extract<
    analytics.SiteAnalyticsEvent,
    { type: 'adaptive_content_enable' }
>['source'];

const SpaceSiteAdaptiveContext =
    React.createContext<SpaceSiteAdaptiveContextValue>(contextDefaultValue);

export function useSpaceSiteAdaptiveContext() {
    return React.useContext(SpaceSiteAdaptiveContext);
}

/**
 * Provide context for adaptive context feature inside the space & editor.
 */
export function SpaceAdaptiveContextProvider(props: {
    organizationId: api.Organization['id'];
    spaceId: api.Space['id'];
    children: React.ReactNode;
}) {
    const { organizationId, spaceId, children } = props;

    const organization = useCurrentOrganization();
    const orgAllContentResult = useQuery(apiQuery('getOrganizationAllContent', { organizationId }));
    const [enableFeatureSource, setEnableFeatureSource] =
        React.useState<EnableAdaptiveContentFeatureSource>();

    const primarySite = useSpacePrimarySite(orgAllContentResult.data ?? null, spaceId);
    const featureIntroDialogTrigger = useDialogTriggerState();
    const editSchemaDialogTrigger = useDialogTriggerState();
    const site = primarySite?.site;
    const openFeatureIntroDialog = useEventCallback(
        (source: EnableAdaptiveContentFeatureSource) => {
            setEnableFeatureSource(source);
            featureIntroDialogTrigger.open();
        }
    );
    const openEditSchemaDialog = useEventCallback(() => editSchemaDialogTrigger.open());

    if (!site) {
        return (
            <SpaceSiteAdaptiveContext.Provider value={contextDefaultValue}>
                {children}
            </SpaceSiteAdaptiveContext.Provider>
        );
    }

    const isUpgradeRequired = isSiteUpgradeRequiredForFeature({
        organization,
        site,
        featureKey: api.SiteFeatureKey.SitesAdaptiveContent,
    });

    return (
        <SpaceSiteAdaptiveContext.Provider
            value={{ site, isUpgradeRequired, openFeatureIntroDialog, openEditSchemaDialog }}
        >
            <SiteContextProvider organizationId={organizationId} site={site}>
                {children}
                <DialogRoot key="adaptive-intro-dialog" {...featureIntroDialogTrigger.rootProps}>
                    <Dialog>
                        <DialogBody>
                            <React.Suspense fallback={<LoadingPanel size="small" />}>
                                <AdaptiveContentFeatureIntro
                                    orientation="vertical"
                                    onFeatureEnabled={() => {
                                        if (enableFeatureSource) {
                                            analytics.logEvent(
                                                analytics.createSiteEvent(
                                                    {
                                                        organization: organizationId,
                                                        id: site.id,
                                                    },
                                                    {
                                                        type: 'adaptive_content_enable',
                                                        source: enableFeatureSource,
                                                    }
                                                )
                                            );
                                        }

                                        featureIntroDialogTrigger.close();
                                    }}
                                />
                            </React.Suspense>
                        </DialogBody>
                    </Dialog>
                </DialogRoot>
                {site.adaptiveContent?.enabled ? (
                    <DialogRoot {...editSchemaDialogTrigger.rootProps}>
                        <AdaptiveContentBuilderDialog
                            initialTab="schema"
                            organizationId={organizationId}
                            siteId={site.id}
                            tabs={[{ tab: 'schema' }]}
                        />
                    </DialogRoot>
                ) : null}
            </SiteContextProvider>
        </SpaceSiteAdaptiveContext.Provider>
    );
}
